import { useState } from 'react';

import { useFcfContributors } from '@/features/decisions/hooks/useFcfContributors';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import { FcfContributorsTable } from './FcfContributorsTable';

interface Props {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  // Singular: useFcfContributors is genuinely a single-comparison-policy endpoint (unlike its
  // FCF/historical-performance/inventory-coverage siblings), so only the first selected
  // candidate is ever queried here.
  experimentId: string;
  // The full comma-joined selection (all up to 5), for the SKU link out to Product/Stocking
  // Locations — using `experimentId` here would silently drop the rest of the selection from
  // that link, and it would stay lost on any trip back to this page via a breadcrumb that
  // carries Product/Stocking's own (now-truncated) search string forward.
  linkExperimentIdRaw: string;
  periodFrom: string;
  periodTo: string;
}

// Owns the contributors table's search state + data so typing in the SKU search
// only re-renders this subtree — not the whole Value Opportunity page (which
// would otherwise redraw the FCF charts/KPIs on every keystroke).
export function FcfContributorsSection({
  scenarioRunId,
  runId,
  refExperimentId,
  experimentId,
  linkExperimentIdRaw,
  periodFrom,
  periodTo,
}: Props) {
  const [skuSearch, setSkuSearch] = useState('');
  const debouncedSkuSearch = useDebouncedValue(skuSearch.trim(), 300);

  const contributors = useFcfContributors({
    scenarioRunId,
    runId,
    refExperimentId,
    experimentId,
    periodFrom,
    periodTo,
    skuContains: debouncedSkuSearch,
  });

  return (
    <FcfContributorsTable
      rows={contributors.rows}
      search={skuSearch}
      onSearchChange={setSkuSearch}
      persona={{ scenarioRunId, runId, refExperimentId, experimentId: linkExperimentIdRaw, periodFrom, periodTo }}
      netFcfImpact={contributors.netFcfImpact}
      pageInfo={contributors.pageInfo}
      page={contributors.page}
      setPage={contributors.setPage}
      pageSize={contributors.pageSize}
      isLoading={contributors.isLoading}
      error={contributors.error}
      elapsedMs={contributors.elapsedMs}
    />
  );
}
